import React from 'react';

import type { NodeData } from '@contentful/rich-text-types';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { kebabCase } from 'lodash-es';

import { Image, Link, Separator, Text } from '@change/ds-core-ui';

import { EmbeddedVideo } from '#src/app/shared/components/embeddedMedia';

// We will need to create an ID for each heading to allow for anchor links
// This is just a test implementation and will need to be updated
const createIdFromChildren = (children: React.ReactNode): string => {
	if (typeof children === 'string') return kebabCase(children);

	if (Array.isArray(children)) {
		return kebabCase(children.map(createIdFromChildren).join(''));
	}

	// If the children is a 'b' element, we need to get the text inside it
	if (React.isValidElement(children) && children.type === 'b') {
		const { props } = children as React.ReactElement<{ children?: React.ReactNode }>;

		if (props.children) {
			return createIdFromChildren(props.children);
		}
	}

	return '';
};

export const articleContentOptions: NodeData = {
	renderMark: {
		[MARKS.BOLD]: (text: string) => <b className="font-bold">{text}</b>,
	},
	renderNode: {
		// Text content
		[BLOCKS.HEADING_1]: (_node: NodeData, children: React.ReactNode) => (
			// To prevent H1 from being used, we'll override and force as H2
			<Text as="h2" id={createIdFromChildren(children)} variant="heading" size="medium" className="pb-6 md:pb-10">
				{children}
			</Text>
		),
		[BLOCKS.HEADING_2]: (_node: NodeData, children: React.ReactNode) => (
			<Text as="h2" id={createIdFromChildren(children)} variant="heading" size="medium" className="pb-6 md:pb-10">
				{children}
			</Text>
		),
		[BLOCKS.HEADING_3]: (_node: NodeData, children: React.ReactNode) => (
			<Text as="h3" variant="heading" size="x-small" className="pb-6 md:pb-10">
				{children}
			</Text>
		),
		[BLOCKS.UL_LIST]: (_node: NodeData, children: React.ReactNode) => (
			<ul className="list-disc pb-6 pl-5 md:pb-10">{children}</ul>
		),
		[BLOCKS.OL_LIST]: (_node: NodeData, children: React.ReactNode) => (
			// Negative margin adjusts the content to align closer to the list marker
			<ol className="list-decimal pb-6 pl-8 **:marker:ml-0 md:pb-10 [&_p]:-ml-2">{children}</ol>
		),
		[BLOCKS.PARAGRAPH]: (_node: NodeData, children: React.ReactNode) => (
			<Text as="p" className="[&:not(ul_*,ol_*,th_*,td_*)]:pb-6">
				{children}
			</Text>
		),
		[INLINES.HYPERLINK]: ({ data: { uri } }: { data: { uri: string } }, children: string) => (
			// Left padding to add spacing as `{' '}` isn't being applied
			<Link data-qa="inline-content-link" className="pl-1" href={uri}>
				{children}
			</Link>
		),
		// Other content
		[BLOCKS.HR]: () => <Separator className="border-stroke-secondary my-0 mb-6 md:mb-10" />,
		[BLOCKS.EMBEDDED_ASSET]: ({
			data: { target },
		}: {
			data: { target?: { description?: string; url?: string; title?: string } };
		}) => {
			return (
				<Image
					className="mb-6 bg-white object-contain md:mb-10"
					alt={target?.description ?? target?.title}
					src={target?.url}
				/>
			);
		},
		[BLOCKS.TABLE]: (_node: NodeData, children: React.ReactNode) => (
			<div className="border-stroke-secondary mt-4 mb-6 overflow-hidden rounded-md border md:mb-10">
				<table className="w-full table-fixed border-collapse">
					<tbody>{children}</tbody>
				</table>
			</div>
		),
		[BLOCKS.TABLE_HEADER_CELL]: (_node: NodeData, children: React.ReactNode) => (
			<th className="bg-background-secondary border-stroke-secondary [&_p]:text-size-body-bold border-r p-3 text-left align-top last:border-r-0">
				{children}
			</th>
		),
		[BLOCKS.TABLE_ROW]: (_node: NodeData, children: React.ReactNode) => (
			<tr className="border-stroke-secondary border-b last:border-b-0">{children}</tr>
		),
		[BLOCKS.TABLE_CELL]: (_node: NodeData, children: React.ReactNode) => (
			<td className="border-stroke-secondary border-r p-3 align-top last:border-r-0">{children}</td>
		),
		[BLOCKS.EMBEDDED_ENTRY]: ({
			data: { target },
		}: {
			data: { target?: { videoId?: string; videoPlatform?: string; id?: string; displayText?: string } };
		}) => {
			if (target?.displayText && target.id) {
				return <div id={target.id} />;
			}

			if (target?.videoPlatform === 'youtube') {
				const html = `<iframe
						src="https://www.youtube-nocookie.com/embed/${target.videoId}"
						title="YouTube video player"
						frameBorder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						referrerPolicy="strict-origin-when-cross-origin"
						allowFullScreen
						className="aspect-video w-full"
					/>`;
				return <EmbeddedVideo embedHtml={html} aspectRatio="16/9" />;
			}
			if (target?.videoPlatform === 'tiktok') {
				const html = `<iframe
						title="TikTok video player"
						src="https://www.tiktok.com/embed/v2/${target.videoId}"
						width="100%"
						height="739"
						frameBorder="0"
						allowFullScreen
					/>`;
				return <EmbeddedVideo embedHtml={html} />;
			}

			return null;
		},
	},
};
